import safeGet from 'just-safe-get'

import { useAuthStore } from '@shared/store/auth'
import { useLocationStore } from '@shared/store/location'
import { getChildNameByPermissions } from '@shared/utils/navigation'
import { requireAuth, noMatch, commonRoutes } from '@shared/router/helper'
import { useAppConfigStore } from '@shared/store/app-config'
import EmptyLoadingComponent from './empty-loading-root.vue'
import { checkPermissions } from '@shared/utils/permissions'

/**
 * Route `meta` carries three independent access checks, all enforced by the global guard in
 * `shared/router/index.js`:
 * - `scopes`      — the user's permissions; redirects to /403.
 * - `featurePath` — account entitlement, from the server driven `featureConfig`.
 * - `rolloutFlag` — rollout gating, resolved through Posthog; redirects to /404 until the flag resolves enabled
 *   for the merchant. Declaring a key from the `FeatureFlags` registry is all that is needed.
 *   Put it on the parent record and it covers every child route. Hide the matching nav entry
 *   with the same key via `rolloutFlag` in `uz-navigation.js`.
 */
const Defaulter = () =>
  import(/* webpackChunkName: "defaulter" */ '@shared/components/defaulter')

/**
 * Payment links entitlement comes from the merchant features, which are cached
 * per tenant, so this only awaits a request on the first navigation.
 */
async function requirePaymentLinksFeature() {
  const authStore = useAuthStore()
  await authStore.waitThInitializated()

  if (!authStore.isAuthenticated) {
    return '/login'
  }

  const locationStore = useLocationStore()
  if (!locationStore.merchantFeaturesFetched) {
    await locationStore.fetchMerchantFeatures()
  }

  const { paylater, linkpay } = locationStore.merchantFeatures
  if (!paylater && !linkpay) {
    return '/404'
  }
}

const CustomDashboardsWrap = () =>
  import(
    /* webpackChunkName: "custom-dashboards" */ '@uz/views/utilities/custom-dashboards/components/custom-content'
  )

const routes = [
  {
    path: '',
    component: EmptyLoadingComponent,
    async beforeEnter() {
      await useAuthStore().waitThInitializated()
      return getDefaultLandingPath()
    }
  },
  {
    path: '/home',
    beforeEnter: async () => {
      await useAuthStore().waitThInitializated()
      // root is special as we don't want to show 401 error, so we go straight to login
      if (!useAuthStore().isAuthenticated) {
        return '/login'
      }
    },
    children: [
      {
        path: '',
        name: 'home',
        beforeEnter: requireAuth,
        component: () =>
          import(/* webpackChunkName: "home" */ '@uz/views/home/home'),
        meta: {
          scopes: ['home:analytics:read']
        }
      }
    ]
  },
  {
    path: '/orders',
    component: () =>
      import(/* webpackChunkName: "reports" */ '@uz/views/orders'),
    children: [
      {
        path: '',
        redirect: () => {
          return getParentRedirectTo(['/orders'])
        }
      },
      {
        path: 'reports_orders',
        component: () =>
          import(
            /* webpackChunkName: "orders_reports_orders" */ '@uz/views/orders/reports_orders'
          ),
        beforeEnter: requireAuth,
        props: () => ({}),
        meta: {
          scopes: ['reports_orders:read']
        },
        children: [
          {
            path: '',
            name: 'reports-orders-list',
            component: () =>
              import(
                /* webpackChunkName: "reports-orders-all" */ '@uz/views/orders/reports_orders/all'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['reports_orders:read'],
              featurePath: 'orders.reports_orders'
            }
          },
          {
            path: ':id',
            redirect: { name: 'reports-orders-detail' }
          },
          {
            path: ':id/detail',
            name: 'reports-orders-detail',
            component: () =>
              import(
                /* webpackChunkName: "reports-orders-detail" */ '@uz/views/orders/reports_orders/detail'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['reports_orders:read'],
              featurePath: 'orders.reports_orders'
            }
          },
          noMatch
        ]
      },
      {
        path: 'orders_transactions',
        component: () =>
          import(
            /* webpackChunkName: "reports_orders_transactions" */ '@uz/views/orders/orders_transactions'
          ),
        beforeEnter: requireAuth,
        props: () => ({}),
        meta: {
          scopes: ['orders_transactions:read']
        },
        children: [
          {
            path: '',
            name: 'reports-orders_transactions-list',
            component: () =>
              import(
                /* webpackChunkName: "reports-orders_transactions-all" */ '@uz/views/orders/orders_transactions/all.vue'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['orders_transactions:read'],
              featurePath: 'orders.orders_transactions'
            }
          },
          {
            path: ':id',
            redirect: { name: 'reports-orders_transactions-detail' }
          },
          {
            path: ':id/detail',
            name: 'reports-orders_transactions-detail',
            component: () =>
              import(
                /* webpackChunkName: "reports-orders_transactions-detail" */ '@uz/views/orders/orders_transactions/detail'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['orders_transactions:read'],
              featurePath: 'orders.orders_transactions'
            }
          }
        ]
      },
      {
        path: 'payment_links',
        name: 'payment-links',
        component: () =>
          import(
            /* webpackChunkName: "payment_links-all" */ '@uz/views/orders/payment_links/all.vue'
          ),
        beforeEnter: requirePaymentLinksFeature,
        props: () => ({}),
        meta: {
          scopes: [
            'settings:payment-link-branding',
            'settings:payment-link-branding:read'
          ]
        }
      },
      {
        path: 'batch_processing',
        component: () =>
          import(
            /* webpackChunkName: "batch_processing" */ '@uz/views/orders/batch_processing'
          ),
        beforeEnter: requireAuth,
        props: () => ({}),
        meta: {
          scopes: ['transaction_batches:read']
        },
        children: [
          {
            path: '',
            name: 'batch-processing-list',
            component: () =>
              import(
                /* webpackChunkName: "batch_processing-all" */ '@uz/views/orders/batch_processing/all.vue'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['transaction_batches:read'],
              featurePath: 'orders.batch_processing'
            }
          }
        ]
      },
      noMatch
    ]
  },
  {
    path: '/account',
    component: () =>
      import(
        /* webpackChunkName: "account_invoices" */ '@uz/views/account/invoices'
      ),
    beforeEnter: requireAuth,
    redirect: '/account/invoices',
    props: () => ({}),
    meta: {
      scopes: ['account:invoices:read']
    },
    children: [
      {
        path: 'invoices',
        name: 'account-invoices',
        component: () =>
          import(
            /* webpackChunkName: "account_invoices_all" */ '@uz/views/account/invoices/all'
          ),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['account:invoices:read']
        }
      },
      noMatch
    ]
  },
  {
    path: '/utilities',
    component: () =>
      import(/* webpackChunkName: "utilities" */ '@uz/views/utilities'),
    beforeEnter: requireAuth,
    children: [
      {
        path: '',
        redirect: () => getParentRedirectTo(['/utilities'])
      },
      {
        path: 'export_history',
        name: 'export-history',
        component: () =>
          import(
            /* webpackChunkName: "utilities_export-history" */ '@uz/views/utilities/export-history'
          ),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['utilities:export_history:read']
        }
      },
      noMatch
    ]
  },
  {
    path: '/staff',
    component: () => import(/* webpackChunkName: "staff" */ '@uz/views/staff'),
    beforeEnter: requireAuth,
    children: [
      {
        path: '',
        redirect: () => getParentRedirectTo(['/staff']),
        meta: {
          scopes: ['staff:read'],
          featurePath: 'staff'
        }
      },
      {
        path: 'manager',
        component: () =>
          import(/* webpackChunkName: "staff" */ '@uz/views/staff'),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['staff:read']
        },
        children: [
          {
            path: '',
            name: 'staff-list',
            component: () =>
              import(/* webpackChunkName: "staff" */ '@uz/views/staff/all'),
            meta: {
              scopes: ['staff:read']
            }
          },
          {
            path: 'new',
            name: 'staff-new',
            component: () =>
              import(
                /* webpackChunkName: "staff_new" */ '@uz/views/staff/edit'
              ),
            meta: {
              scopes: ['staff:create']
            }
          },
          {
            path: ':id',
            redirect: { name: 'staff-edit' }
          },
          {
            path: ':id/edit',
            name: 'staff-edit',
            component: () =>
              import(
                /* webpackChunkName: "staff_edit" */ '@uz/views/staff/edit'
              ),
            meta: {
              scopes: ['staff:read']
            }
          }
        ]
      },
      {
        path: 'staff_groups',
        component: () =>
          import(
            /* webpackChunkName: "staff-groups" */ '@uz/views/staff-groups'
          ),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['staff:groups:read']
        },
        children: [
          {
            path: '',
            name: 'staff-groups-list',
            component: () =>
              import(
                /* webpackChunkName: "staff-groups" */ '@uz/views/staff-groups/all'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['staff:groups:read']
            }
          },
          {
            path: 'new',
            name: 'staff-group-new',
            component: () =>
              import(
                /* webpackChunkName: "staff-groups_edit" */ '@uz/views/staff-groups/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['staff:groups:create']
            }
          },
          {
            path: ':id',
            redirect: { name: 'staff-group-edit' }
          },
          {
            path: ':id/edit',
            name: 'staff-group-edit',
            component: () =>
              import(
                /* webpackChunkName: "staff-groups_edit" */ '@uz/views/staff-groups/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['staff:groups:read']
            }
          },
          noMatch
        ]
      },
      {
        path: 'shift_plan',
        name: 'shift-plan',
        component: () => import('@uz/views/shift-plan/index.vue'),
        meta: {
          scopes: ['staff:shift_plans:read']
        },
        children: [
          {
            path: '',
            name: 'shift-plan-list',
            component: () => import('@uz/views/shift-plan/all.vue'),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['staff:shift_plans:read']
            }
          },
          {
            path: ':branch',
            redirect: { name: 'shift-plan-edit' }
          },
          {
            path: ':branch/edit',
            name: 'shift-plan-edit',
            component: () => import('@uz/views/shift-plan/edit.vue'),
            meta: {
              scopes: ['staff:shift_plans:read']
            }
          }
        ],
        beforeEnter: requireAuth
      },
      noMatch
    ]
  },
  {
    path: '/customers',
    component: () =>
      import(/* webpackChunkName: "customers" */ '@uz/views/customers'),
    beforeEnter: requireAuth,
    children: [
      {
        path: '',
        name: 'customers-list',
        component: () =>
          import(
            /* webpackChunkName: "customers-all" */ '@uz/views/customers/all'
          ),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['customers:read']
        }
      },
      {
        path: 'new',
        name: 'customer-new',
        component: () =>
          import(
            /* webpackChunkName: "customer_edit" */ '@uz/views/customers/edit'
          ),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['customers:create']
        }
      },
      {
        path: ':id',
        component: () =>
          import(
            /* webpackChunkName: "customer_edit" */ '@uz/views/customers/edit'
          ),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['customers:read']
        }
      },
      noMatch
    ]
  },
  {
    path: '/reservations',
    component: () =>
      import(/* webpackChunkName: "reservations" */ '@uz/views/reservations'),
    beforeEnter: requireAuth,
    children: [
      {
        path: '',
        redirect: () => getParentRedirectTo(['/reservations'])
      },
      {
        path: 'calendar',
        component: () =>
          import(
            /* webpackChunkName: "reservations_calendar" */ '@uz/views/reservations/calendar'
          ),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['reservations:calendar:read']
        },
        children: []
      },
      {
        path: 'service_category',
        component: () =>
          import(
            /* webpackChunkName: "reservations" */ '@uz/views/reservations'
          ),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['reservations:service_categories:read'],
          featurePath: 'reservations'
        },
        children: [
          {
            path: '',
            name: 'service-category',
            beforeEnter: requireAuth,
            meta: {
              scopes: ['reservations:service_categories:read'],
              featurePath: 'reservations'
            },
            component: () =>
              import(
                /* webpackChunkName: "reservations_service_category_all" */ '@uz/views/reservations/service-category/all'
              )
          },
          {
            path: 'new',
            name: 'category-new',
            component: () =>
              import(
                /* webpackChunkName: "reservations_service_category_edit" */ '@uz/views/reservations/service-category/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['reservations:service_categories:create'],
              featurePath: 'reservations'
            }
          },
          {
            path: ':id',
            redirect: { name: 'service-category-edit' }
          },
          {
            path: ':id/edit',
            name: 'service-category-edit',
            component: () =>
              import(
                /* webpackChunkName: "reservations_service_category_edit" */ '@uz/views/reservations/service-category/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['reservations:service_categories:read'],
              featurePath: 'reservations'
            }
          }
        ]
      },
      {
        path: 'services',
        component: () =>
          import(
            /* webpackChunkName: "reservations" */ '@uz/views/reservations'
          ),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['reservations:services:read'],
          featurePath: 'reservations'
        },
        children: [
          {
            path: '',
            name: 'services',
            component: () =>
              import(
                /* webpackChunkName: "reservations_services_all" */ '@uz/views/reservations/services/all'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['reservations:services:read'],
              featurePath: 'reservations'
            }
          },
          {
            path: 'new',
            name: 'services-new',
            component: () =>
              import(
                /* webpackChunkName: "reservations_services_edit" */ '@uz/views/reservations/services/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['reservations:services:create'],
              featurePath: 'reservations'
            }
          },
          {
            path: ':id',
            redirect: { name: 'services-edit' }
          },
          {
            path: ':id/edit',
            name: 'services-edit',
            component: () =>
              import(
                /* webpackChunkName: "reservations_services_edit" */ '@uz/views/reservations/services/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['reservations:services:read'],
              featurePath: 'reservations'
            }
          }
        ]
      },
      {
        path: 'calendar',
        name: 'calendar',
        component: () => import('@uz/views/reservations/calendar/index.vue'),
        meta: {
          scopes: ['reservations:calendar:read']
        }
      },
      noMatch
    ]
  },
  {
    path: '/products',
    component: () =>
      import(/* webpackChunkName: "products" */ '@uz/views/products'),
    beforeEnter: requireAuth,
    children: [
      {
        path: '',
        redirect: () => getParentRedirectTo(['/products'])
      },
      {
        path: 'manager',
        component: () =>
          import(/* webpackChunkName: "products" */ '@uz/views/products'),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['products:read'],
          featurePath: 'products.manager'
        },
        children: [
          {
            path: '',
            name: 'products-manager',
            component: () =>
              import(
                /* webpackChunkName: "products_all" */ '@uz/views/products/all'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:read']
            }
          },
          {
            path: 'bulk_edit',
            name: 'products-bulk-edit',
            component: () =>
              import(
                /* webpackChunkName: "products_bulk_edit" */ '@uz/views/products/components/bulk-edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:update']
            }
          },
          {
            path: 'duplicate',
            name: 'products-duplicate',
            component: () =>
              import(
                /* webpackChunkName: "products_duplicate" */ '@uz/views/products/components/duplicate'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:update']
            }
          },
          {
            path: 'duplicate',
            name: 'products-duplicate',
            component: () =>
              import(
                /* webpackChunkName: "products_duplicate" */ '@uz/views/products/components/duplicate'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:update']
            }
          },
          {
            path: 'new',
            name: 'products-new',
            component: () =>
              import(
                /* webpackChunkName: "products_edit_v2" */ '@uz/views/products/v2/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:create']
            }
          },
          {
            path: ':id',
            redirect: { name: 'products-edit' }
          },
          {
            path: ':id/edit',
            name: 'products-edit',
            component: () =>
              import(
                /* webpackChunkName: "products_edit_v2" */ '@uz/views/products/v2/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:read']
            }
          },
          {
            path: 'custom_dashboards/:name',
            name: 'products-custom-dashboards',
            component: CustomDashboardsWrap,
            beforeEnter: requireAuth,
            meta: {
              scopes: ['utilities:custom_dashboard:read']
            }
          },
          noMatch
        ]
      },
      {
        path: 'product_groups',
        component: () =>
          import(
            /* webpackChunkName: "product_groups" */ '@uz/views/product-groups'
          ),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['products:product_groups:read'],
          featurePath: 'products.product_groups'
        },
        children: [
          {
            path: '',
            name: 'products-product-groups-list',
            component: () =>
              import(
                /* webpackChunkName: "products-product-groups-list" */ '@uz/views/product-groups/all'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:product_groups:read']
            }
          },
          {
            path: 'new',
            name: 'products-product-groups-new',
            component: () =>
              import(
                /* webpackChunkName: "products-product-group-edit" */ '@uz/views/product-groups/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:product_groups:create']
            }
          },
          {
            path: ':id',
            name: 'products-product-group-display',
            redirect: { name: 'products-product-groups-edit' },
            scopes: ['products:product_groups:read']
          },
          {
            path: ':id/edit',
            name: 'products-product-groups-edit',
            component: () =>
              import(
                /* webpackChunkName: "products-product-group-edit" */ '@uz/views/product-groups/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:product_groups:read']
            }
          }
        ]
      },
      {
        path: 'product_templates',
        name: 'products-product-templates',
        component: () =>
          import(
            /* webpackChunkName: "product_templates" */ '@uz/views/product-templates'
          ),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['products:product_templates:read'],
          featurePath: 'products.product_templates'
        },
        children: [
          {
            path: '',
            name: 'products-product-templates-all',
            component: () =>
              import(
                /* webpackChunkName: "products-product-templates-all" */ '@uz/views/product-templates/all'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:product_templates:read']
            }
          },
          {
            path: '/products/product_templates/:id',
            name: 'products-product-templates-edit',
            component: () =>
              import(
                /* webpackChunkName: "products-product-templates-edit" */ '@uz/views/product-templates/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:product_templates:read']
            }
          }
        ]
      },
      {
        path: 'product_service_questions',
        name: 'products-service-questions',
        component: () =>
          import(
            /* webpackChunkName: "product-services_questions" */ '@uz/views/product-services/'
          ),
        meta: {
          scopes: ['products:service_questions:read'],
          featurePath: 'products.service.questions'
        },
        children: [
          {
            path: '',
            name: 'products-service-questions-list',
            component: () =>
              import(
                /* webpackChunkName: "product-services_questions" */ '@uz/views/product-services/questions/all'
              ),
            meta: {
              scopes: ['products:service_questions:read']
            }
          },
          {
            path: 'new',
            name: 'products-service-questions-new',
            component: () =>
              import(
                /* webpackChunkName: "product-services_questions" */ '@uz/views/product-services/questions/edit'
              ),
            meta: {
              scopes: ['products:service_questions:create']
            }
          },
          {
            path: ':id',
            name: 'products-service-questions-edit',
            component: () =>
              import(
                /* webpackChunkName: "product-services_questions" */ '@uz/views/product-services/questions/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:service_questions:read']
            }
          }
        ]
      },
      {
        path: 'discounts',
        component: () =>
          import(/* webpackChunkName: "discounts" */ '@uz/views/discounts'),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['products:discounts:read'],
          featurePath: 'products.discounts'
        },
        children: [
          {
            path: '',
            name: 'discounts-list',
            component: () =>
              import(
                /* webpackChunkName: "discounts" */ '@uz/views/discounts/all'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:discounts:read']
            }
          },
          // V2
          {
            path: 'new',
            name: 'discounts-new',
            component: () =>
              import(
                /* webpackChunkName: "discounts_edit" */ '@uz/views/discounts/v2/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:discounts:create']
            }
          },
          {
            path: ':id',
            redirect: { name: 'discount-edit' }
          },
          {
            path: ':id/edit',
            name: 'discount-edit',
            component: () =>
              import(
                /* webpackChunkName: "discounts_edit" */ '@uz/views/discounts/v2/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:discounts:read']
            }
          },
          // V1
          {
            path: 'new-v1',
            name: 'discounts-new-v1',
            component: () =>
              import(
                /* webpackChunkName: "discounts_edit_v1" */ '@uz/views/discounts/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:discounts:create']
            }
          },
          {
            path: ':id/edit-v1',
            name: 'discount-edit-v1',
            component: () =>
              import(
                /* webpackChunkName: "discounts_edit_v1" */ '@uz/views/discounts/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:discounts:read']
            }
          },
          noMatch
        ]
      },
      {
        path: 'pricebooks',
        component: () =>
          import(
            /* webpackChunkName: "pricebooks_list" */ '@uz/views/pricebooks'
          ),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['products:pricebooks:read'],
          featurePath: 'products.pricebooks'
        },
        children: [
          {
            path: '',
            name: 'pricebooks-list',
            component: () =>
              import(
                /* webpackChunkName: "pricebooks" */ '@uz/views/pricebooks/all'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:pricebooks:read']
            }
          },
          {
            path: 'new',
            name: 'pricebooks-new',
            component: () =>
              import(
                /* webpackChunkName: "pricebooks_edit" */ '@uz/views/pricebooks/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:pricebooks:create']
            }
          },
          {
            path: ':id/edit',
            name: 'pricebook-edit',
            component: () =>
              import(
                /* webpackChunkName: "pricebooks_edit" */ '@uz/views/pricebooks/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:pricebooks:read']
            }
          },
          {
            path: ':id',
            redirect: { name: 'pricebook-edit' }
          },
          noMatch
        ]
      },
      {
        path: 'promotions',
        component: () =>
          import(/* webpackChunkName: "promotions" */ '@uz/views/promotions'),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['products:promotions:read']
        },
        children: [
          {
            path: '',
            name: 'promotions-list',
            component: () =>
              import(
                /* webpackChunkName: "promotions" */ '@uz/views/promotions/all'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:promotions:read'],
              featurePath: 'products.promotions'
            }
          },
          {
            path: 'new',
            name: 'promotion-new',
            component: () =>
              import(
                /* webpackChunkName: "promotion_edit" */ '@uz/views/promotions/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:promotions:create']
            }
          },
          {
            path: ':id',
            redirect: { name: 'promotion-edit' }
          },
          {
            path: ':id/edit',
            name: 'promotion-edit',
            component: () =>
              import(
                /* webpackChunkName: "promotion_edit" */ '@uz/views/promotions/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:promotions:read']
            }
          },
          noMatch
        ]
      },
      {
        path: 'tags',
        name: 'products-tags',
        beforeEnter: requireAuth,
        meta: {
          scopes: ['products:tags:read'],
          featurePath: 'products.tags'
        },
        children: [
          {
            path: '',
            name: 'products-tags-all',
            component: () =>
              import(/* webpackChunkName: "tags" */ '@uz/views/tags'),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:tags:read'],
              featurePath: 'products.tags'
            }
          },
          {
            path: 'new',
            name: 'products-tags-new',
            component: () =>
              import(/* webpackChunkName: "tags" */ '@uz/views/tags'),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:tags:create']
            }
          },
          {
            path: ':id',
            name: 'products-tags-edit',
            component: () =>
              import(/* webpackChunkName: "tags" */ '@uz/views/tags'),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:tags:read']
            }
          }
        ]
      },
      {
        path: 'addon-groups',
        name: 'products-addon-groups',
        component: () =>
          import(
            /* webpackChunkName: "product_addon_groups" */ '@uz/views/product-addon-groups'
          ),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['products:addon_groups:read'],
          featurePath: 'products.addon_groups'
        },
        children: [
          {
            path: '',
            name: 'products-addon-groups-list',
            component: () =>
              import(
                /* webpackChunkName: "product_addon_groups" */ '@uz/views/product-addon-groups/all'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:addon_groups:read']
            }
          },
          {
            path: 'new',
            name: 'products-addon-groups-new',
            component: () =>
              import(
                /* webpackChunkName: "product_addon_groups" */ '@uz/views/product-addon-groups/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:addon_groups:create']
            }
          },
          {
            path: ':id',
            redirect: { name: 'products-addon-groups-edit' }
          },
          {
            path: ':id/edit',
            name: 'products-addon-groups-edit',
            component: () =>
              import(
                /* webpackChunkName: "product_addon_groups" */ '@uz/views/product-addon-groups/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:addon_groups:read']
            }
          },
          noMatch
        ]
      }
    ]
  },
  {
    path: '/user_profile',
    component: () =>
      import(/* webpackChunkName: "user_profile" */ '@uz/views/user-profile'),
    beforeEnter: requireAuth,
    meta: {
      async beforeResolve() {
        const authStore = useAuthStore()
        const staff = !!safeGet(authStore, 'staff')
        const isSubUser = !!safeGet(authStore, 'thUserId')
        if (isSubUser && staff) {
          return
        }
        return '/404'
      }
    },
    children: [
      {
        path: '',
        name: 'user-profile',
        component: () =>
          import(
            /* webpackChunkName: "user-profile" */ '@uz/views/user-profile/main'
          ),
        children: [
          {
            path: 'sales',
            name: 'sales',
            component: () =>
              import(
                /* webpackChunkName: "user-profile" */ '@uz/views/user-profile/main'
              )
          },
          {
            path: 'hours',
            name: 'hours',
            component: () =>
              import(
                /* webpackChunkName: "user-profile" */ '@uz/views/user-profile/main'
              )
          }
        ],
        beforeEnter: requireAuth
      },
      noMatch
    ]
  },
  {
    path: '/me',
    name: 'me',
    component: () => import(/* webpackChunkName: "me" */ '@uz/views/me'),
    beforeEnter: requireAuth
  },
  {
    path: '/settings',
    component: () =>
      import(/* webpackChunkName: "settings" */ '@uz/views/settings'),
    beforeEnter: requireAuth,
    children: [
      {
        path: '',
        redirect: () => getParentRedirectTo(['/settings'])
      },
      {
        name: 'dashboard',
        path: 'dashboard',
        component: () =>
          import(
            /* webpackChunkName: "settings_dashboard" */ '@uz/views/settings/sections/dashboard'
          ),
        beforeEnter: requireAuth,
        meta: {
          scopes: [
            'settings:dashboard',
            'settings:dashboard:general',
            'settings:dashboard:general:read',
            'settings:dashboard:search_results',
            'settings:dashboard:search_results:read',
            'settings:dashboard:customers',
            'settings:dashboard:customers:read',
            'settings:dashboard:staff',
            'settings:dashboard:staff:read',
            'settings:dashboard:products',
            'settings:dashboard:products:read',
            'settings:dashboard:reservations',
            'settings:dashboard:reservations:read'
          ]
        },
        children: [
          {
            path: '',
            name: 'settings-dashboard',
            redirect: () => getParentRedirectTo(['/settings', 'dashboard'])
          },
          {
            path: 'general',
            name: 'settings-dashboard-general',
            component: () =>
              import(
                /* webpackChunkName: "settings_dashboard_general" */ '@uz/views/settings/sections/dashboard/general'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:dashboard:general:read']
            }
          },
          {
            path: 'searches',
            name: 'settings-dashboard-searches',
            component: () =>
              import(
                /* webpackChunkName: "settings_dashboard_searches" */ '@uz/views/settings/sections/dashboard/searches'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:dashboard:search_results:read']
            }
          },
          {
            path: 'customers',
            name: 'settings-dashboard-customers',
            component: () =>
              import(
                /* webpackChunkName: "settings_dashboard_customers" */ '@uz/views/settings/sections/dashboard/customers'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:dashboard:customers:read']
            }
          },
          {
            path: 'products',
            name: 'settings-dashboard-products',
            component: () =>
              import(
                /* webpackChunkName: "settings_dashboard_products" */ '@uz/views/settings/sections/dashboard/products'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:dashboard:products:read']
            }
          },
          {
            path: 'staff',
            name: 'settings-dashboard-staff',
            component: () =>
              import(
                /* webpackChunkName: "settings_dashboard_staff" */ '@uz/views/settings/sections/dashboard/staff'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:dashboard:staff:read']
            }
          }
        ]
      },
      {
        name: 'reservations',
        path: 'reservations',
        component: () =>
          import('@uz/views/settings/sections/reservations/beauty/index.vue'),
        beforeEnter: requireAuth,
        children: [
          {
            path: '',
            name: 'settings-reservations',
            redirect: () => getParentRedirectTo(['/settings', 'reservations'])
          },
          {
            path: 'customization',
            name: 'settings-reservations-customization',
            component: () =>
              import('@uz/views/settings/sections/reservations/beauty/customization/index.vue'),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:dashboard:reservations:read']
            }
          },
          {
            path: 'opening-hours',
            name: 'settings-reservations-opening-hours',
            component: () =>
              import('@uz/views/settings/sections/reservations/beauty/opening-hours/index.vue'),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:dashboard:reservations:read']
            }
          },
          {
            path: 'appointment-reminders',
            name: 'settings-reservations-appointment-reminders',
            component: () =>
              import('@uz/views/settings/sections/reservations/beauty/appointment-reminders/index.vue'),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:dashboard:reservations:read']
            }
          }
        ]
      },
      {
        path: 'iam-users',
        component: Defaulter,
        beforeEnter: requireAuth,
        meta: {
          scopes: ['settings:iam-users:read']
        },
        children: [
          {
            path: '',
            name: 'settings-iam-users',
            component: () =>
              import(
                /* webpackChunkName: "settings_users" */ '@uz/views/settings/sections/iam-users/all'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:iam-users:read']
            }
          },
          {
            path: 'new',
            name: 'settings-iam-users-new',
            component: () =>
              import(
                /* webpackChunkName: "settings_users_edit" */ '@uz/views/settings/sections/iam-users/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:iam-users:create']
            }
          },
          {
            path: ':id',
            redirect: { name: 'settings-iam-users-edit' }
          },
          {
            path: ':id/edit',
            name: 'settings-iam-users-edit',
            component: () =>
              import(
                /* webpackChunkName: "settings_users_edit" */ '@uz/views/settings/sections/iam-users/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:iam-users:read']
            }
          }
        ]
      },
      {
        path: 'iam-api-keys',
        component: Defaulter,
        beforeEnter: requireAuth,
        meta: {
          scopes: ['settings:api_keys:list']
        },
        children: [
          {
            path: '',
            name: 'settings-iam-api-keys',
            component: () =>
              import(
                /* webpackChunkName: "iam_api_keys" */ '@uz/views/settings/sections/iam-api-keys/all'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:api_keys:list']
            }
          },
          {
            path: ':id',
            name: 'settings-iam-api-keys-detail',
            component: () =>
              import(
                /* webpackChunkName: "iam_api_keys_edit" */ '@uz/views/settings/sections/iam-api-keys/detail'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:api_keys:list']
            }
          }
        ]
      },
      {
        path: 'payment-link-branding',
        component: Defaulter,
        beforeEnter: requireAuth,
        meta: {
          scopes: ['settings:payment-link-branding:settings:read']
        },
        children: [
          {
            path: '',
            name: 'settings-payment-link-branding',
            component: () =>
              import('@uz/views/settings/sections/payment-link-branding/index.vue'),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:payment-link-branding:settings:read']
            }
          }
        ]
      },
      noMatch
    ]
  },

  ...commonRoutes,
  noMatch
]

function getParentRedirectTo(pathToChildren) {
  const { role, scopes } = useAuthStore()
  const { featureLock, featureConfig } = useAppConfigStore()

  return getChildNameByPermissions(
    pathToChildren,
    routes,
    role,
    scopes,
    featureConfig,
    featureLock
  )
}

function normalizeAppPath(path) {
  if (!path) return '/'
  return path.startsWith('/') ? path : `/${path}`
}

function getDefaultLandingPath() {
  const authStore = useAuthStore()
  const canAccessHome = checkPermissions(
    { scopes: ['home:analytics:read'] },
    authStore.role,
    authStore.scopes
  )
  if (canAccessHome) {
    return '/home'
  }

  return normalizeAppPath(
    getParentRedirectTo([getFirstAvailableParentPath() || '/orders'])
  )
}

function isRouteAccessible(route) {
  const authStore = useAuthStore()
  const leaf = route?.matched?.at(-1)
  const routeScopes = leaf?.meta?.scopes

  if (!routeScopes?.length) {
    return true
  }

  return checkPermissions(
    { scopes: routeScopes },
    authStore.role,
    authStore.scopes
  )
}

async function resolvePostDashboardSwitchNavigation(router) {
  const authStore = useAuthStore()
  await authStore.waitThInitializated()
  await authStore.waitForMeData()

  if (isRouteAccessible(router.currentRoute.value)) {
    return null
  }

  return getDefaultLandingPath()
}

function getFirstAvailableParentPath() {
  return [
    '/orders',
    '/account',
    '/products',
    '/customers',
    '/staff',
    '/reservations',
    '/utilities',
    '/settings'
  ].find((path) => !getParentRedirectTo([path]).includes('catchAll(.*)'))
}

export {
  getDefaultLandingPath,
  getFirstAvailableParentPath,
  isRouteAccessible,
  resolvePostDashboardSwitchNavigation
}
export default routes
